import { flexRender } from "@tanstack/react-table";
import { FC, Fragment, ReactNode, useRef } from "react";
import { cx } from "shared/utils";
import type { Table as ITable, Row, SortingState } from "@tanstack/react-table";
import { EmptyStateRow } from "./component/EmptyStateRow";
import { UntitledIcon } from "./type";
import { useResizeObserver } from "shared/hooks/use-resize-observer";
import { TableHeader } from "./component/TableHeader";
import { ExpandedRow } from "./component/ExpandedRow";
import { SkeletonBody } from "./component/SkeletonBody";
import { Card } from "shared/components/Card";
import { SkeletonCard } from "./component/SkeletonCard";
import { Hide } from "shared/components/helpers/Hide";
import { getNestedValue, markFirstOfType } from "./utils";
import { Show } from "shared/components";

type Props = {
  table: ITable<any>;
  onRowClick?: (rowId: string, rowData?: any) => void;
  emptyStateTitle?: string;
  emptyStateText?: string;
  emptyStateButtonText?: string;
  emptyStateButtonAction?: () => void;
  expandedRenderer?: (row: Row<unknown>) => ReactNode;
  EmptyStateIcon?: UntitledIcon;
  thClassName?: string;
  tdClassName?: string;
  isLoading?: boolean;
  sortableKeys?: string[];
  // hack need to refactor
  sorted?: { key: string; desc: boolean } | SortingState;
  setSorted?: ((prev: { key: string; desc: boolean }) => void) | any;
  hasSubheader?: boolean;
  emptyRowsNumber?: number;
  borderLess?: boolean;
  maw?: number;
  rowHeight?: string;
  isCustomExpandedRow?: boolean;
  mobileRow?: (rowOriginal: any) => ReactNode;
  mobileRowClassName?: string;
  tableClassName?: string;
  fixedHeader?: boolean;
  customFooter?: ReactNode;
  disableMobileListView?: boolean;
  getRowClassName?: (row: Row<any>) => string;

  groupBy?: string;
  innerTitle?: ReactNode;
  groupByDisplayMap?: Record<string, ((v: unknown) => ReactNode) | undefined>;
  borderLessRow?: boolean;
};

// YO, readme please

// column cell definition has some custom shit with columnId
// if id includes 'right-align' -- header-cell will be right aligned
// if it includes custom_ -- it will rended cell without any styles
// action-cell -- adds context menu styles
// expander-cell -- add support for expanding rows.

const PAGE_PADDING = 48;
const MINIMIZED_SIDEBAR_WIDTH = 80;
const SIDEBAR_WIDTH = 240;

export const Table: FC<Props> = ({
  groupBy,
  groupByDisplayMap,
  disableMobileListView,
  table,
  onRowClick,
  expandedRenderer,
  emptyStateTitle,
  emptyStateText,
  EmptyStateIcon,
  thClassName,
  tdClassName,
  emptyStateButtonText,
  emptyStateButtonAction,
  isLoading,
  sortableKeys,
  sorted,
  setSorted,
  hasSubheader,
  emptyRowsNumber = 10,
  borderLess,
  maw: _maw,
  rowHeight = "48px",
  isCustomExpandedRow,
  mobileRow,
  mobileRowClassName,
  tableClassName,
  fixedHeader,
  customFooter,
  getRowClassName,
  innerTitle,
  borderLessRow
}) => {
  const { width } = useResizeObserver();
  const ref = useRef<HTMLDivElement | null>(null);
  // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
  if (!table) return null;

  // To counter media for sidebar
  const sidebarWidth =
    (width || 0) < 1024 ? MINIMIZED_SIDEBAR_WIDTH : SIDEBAR_WIDTH;

  const rowModel = table.getRowModel();
  const headerGroup = table.getHeaderGroups();
  const footerGroup = table.getFooterGroups();

  const maw = _maw || (width || 0) - sidebarWidth - PAGE_PADDING;

  const numcols = table.getAllColumns().length;

  return (
    <>
      <div
        className={cx(
          "overflow-x-auto overscroll-x-none rounded-lg bg-content-1000-inverted",
          borderLess ? "" : "shadow-md outline outline-1 outline-alpha-40",
          fixedHeader ? "relative" : "",
          disableMobileListView ? "block" : "md:block hidden",
          tableClassName
        )}
        ref={ref}
      >
        {innerTitle ?? null}
        <table
          className={cx(
            "w-full bg-content-1000-inverted",
            rowModel.rows.length ? "h-full" : "h-[668px]"
          )}
        >
          <TableHeader
            fixedHeader={fixedHeader}
            headerGroup={headerGroup}
            thClassName={thClassName}
            sortableKeys={sortableKeys}
            sorted={sorted}
            setSorted={setSorted}
            hasSubheader={hasSubheader}
          />
          <tbody className="relative">
            {isLoading ? (
              <SkeletonBody
                rowHeight={rowHeight}
                emptyRowsNumber={emptyRowsNumber}
                headerGroup={headerGroup}
                tdClassName={tdClassName}
                onRowClick={onRowClick}
              />
            ) : rowModel.rows.length ? (
              <>
                {markFirstOfType(rowModel.rows, groupBy).map(
                  (row, row_idx: number) => {
                    const isExpanded = row.getIsExpanded();
                    const isLastRow = row_idx === rowModel.rows.length - 1;
                    const groupBySafeValue = groupBy || "";
                    return (
                      <Fragment key={`row_${row.id}_${row_idx}`}>
                        <Show if={row.isFirstOfAType}>
                          <tr
                            style={{ height: rowHeight || "48px" }}
                            className={cx(
                              "text-content-100 bg-background-900",
                              !borderLessRow &&
                                !(isLastRow && footerGroup.length > 0)
                                ? "border-b border-alpha-40"
                                : ""
                            )}
                          >
                            <td colSpan={numcols} className="pl-10 ">
                              <p className="text-content-400 text-sm font-medium">
                                {groupByDisplayMap &&
                                groupByDisplayMap[groupBySafeValue]
                                  ? groupByDisplayMap[groupBySafeValue](
                                      getNestedValue(
                                        row.original,
                                        groupBy || ""
                                      )
                                    )
                                  : String(
                                      getNestedValue(
                                        row.original,
                                        groupBy || ""
                                      )
                                    )}
                              </p>
                            </td>
                          </tr>
                        </Show>
                        <tr
                          onClick={() =>
                            onRowClick && onRowClick(row.original?.id, row)
                          }
                          style={{
                            height: rowHeight || "48px"
                          }}
                          className={cx(
                            "text-content-100",
                            !borderLessRow &&
                              !(isLastRow && footerGroup.length > 0)
                              ? "border-b border-alpha-40"
                              : "",
                            isExpanded ? "mb-[199px] pb-[100px]" : "",
                            onRowClick
                              ? "cursor-pointer hover:bg-alpha-40"
                              : "",
                            getRowClassName ? getRowClassName(row) : ""
                          )}
                        >
                          {row.getVisibleCells().map((cell, idx) => {
                            const cellContent = flexRender(
                              cell.column.columnDef.cell,
                              cell.getContext()
                            );
                            const isActionCell =
                              cell.column.id === "popover-action" ||
                              cell.column.id === "expander";

                            const size = cell.column.getSize();

                            const fixedWidth =
                              cell.column.id.includes("fixed_width") ||
                              isActionCell
                                ? size
                                : undefined;
                            const isCustomCell =
                              cell.column.id.includes("custom_");
                            return (
                              <td
                                key={`row_${row.id}_cell_${cell.id}_${idx}`}
                                style={
                                  size && !isNaN(size)
                                    ? {
                                        minWidth: size,
                                        width: fixedWidth
                                      }
                                    : undefined
                                }
                              >
                                {isCustomCell ? (
                                  cellContent
                                ) : (
                                  <div
                                    className={cx(
                                      "box-border animate-appear flex h-12 max-h-12 items-center w-full",
                                      isActionCell
                                        ? "ml-auto xl:w-12 w-10 shrink-0 px-0"
                                        : "xl:px-4 px-1",
                                      tdClassName
                                    )}
                                  >
                                    {isActionCell ? (
                                      cellContent
                                    ) : (
                                      <div className="m-0 text-left w-full text-sm font-normal text-content-500">
                                        {cellContent}
                                      </div>
                                    )}
                                  </div>
                                )}
                              </td>
                            );
                          })}
                        </tr>
                        <ExpandedRow
                          table={table}
                          rowId={row.id}
                          isExpanded={isExpanded}
                          expandedRenderer={expandedRenderer}
                          row={row}
                          isCustomExpandedRow={isCustomExpandedRow}
                        />
                      </Fragment>
                    );
                  }
                )}
                {footerGroup.length > 0 &&
                footerGroup[0].headers.some(
                  (h) => h.column.columnDef.footer
                ) ? (
                  <tr>
                    {footerGroup[0].headers.map((footer) => (
                      <td key={`footer_${footer.id}`}>
                        <div className="m-0 text-left text-sm text-content-500">
                          {flexRender(
                            footer.column.columnDef.footer,
                            footer.getContext()
                          )}
                        </div>
                      </td>
                    ))}
                  </tr>
                ) : null}
              </>
            ) : (
              <EmptyStateRow
                maw={maw}
                Icon={EmptyStateIcon}
                title={emptyStateTitle}
                text={emptyStateText}
                buttonText={emptyStateButtonText}
                buttonAction={emptyStateButtonAction}
              />
            )}
          </tbody>

          {customFooter ? (
            <tfoot
              className={cx(
                "animate-appear bg-white w-full",
                fixedHeader ? "sticky bottom-0" : ""
              )}
            >
              <tr>
                <td colSpan={numcols}>{customFooter}</td>
              </tr>
            </tfoot>
          ) : null}
        </table>
      </div>

      <Hide if={disableMobileListView}>
        <div className="block md:hidden">
          {/* Render list view with tailwind */}
          <div className="flex flex-col gap-4">
            {isLoading ? (
              <SkeletonCard />
            ) : (
              rowModel.rows.map((row, row_idx: number) => {
                return (
                  <Fragment key={`row_${row.id}_${row_idx}`}>
                    <Card
                      onClick={() =>
                        onRowClick && onRowClick(row.original?.id, row)
                      }
                      className={cx(
                        "animate-appear flex flex-col border-b border-alpha-40 p-6 overflow-x-hidden",
                        onRowClick ? "cursor-pointer hover:bg-alpha-40" : "",
                        mobileRowClassName
                      )}
                    >
                      {mobileRow
                        ? mobileRow(row)
                        : row.getVisibleCells().map((cell, idx) => {
                            const cellContent = flexRender(
                              cell.column.columnDef.cell,
                              cell.getContext()
                            );
                            const isActionCell =
                              cell.column.id === "popover-action" ||
                              cell.column.id === "expander";

                            const isCustomCell =
                              cell.column.id.includes("custom_");
                            return (
                              <div
                                key={`row_${row.id}_cell_${cell.id}_${idx}`}
                                className={cx(
                                  "box-border animate-appear flex h-12 max-h-12 items-center w-full",
                                  isActionCell
                                    ? "md:ml-auto xl:w-12 w-10 shrink-0 px-0"
                                    : "xl:px-4 px-1",
                                  tdClassName
                                )}
                              >
                                {isCustomCell ? (
                                  cellContent
                                ) : (
                                  <div className="m-0 text-left w-full text-sm font-normal text-content-500">
                                    {cellContent}
                                  </div>
                                )}
                              </div>
                            );
                          })}
                    </Card>
                  </Fragment>
                );
              })
            )}
          </div>
        </div>
      </Hide>
    </>
  );
};
